import { useEffect, useRef, useState, type ReactNode } from 'react';

const SIZE = 6;

/**
 * 6 位验证码输入。
 *
 * 拆成 6 格是为了自动跳格和整串粘贴——邮件里的验证码大多是复制过来的，
 * 单个输入框会让粘贴变成「一格里挤了 6 个字符」。
 */
export function CodeInput({
  value,
  onChange,
  invalid = false,
  autoFocus = false,
}: {
  value: string;
  onChange: (next: string) => void;
  invalid?: boolean;
  autoFocus?: boolean;
}): ReactNode {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) boxes.current[0]?.focus();
  }, [autoFocus]);

  const digits = Array.from({ length: SIZE }, (_unused, i) => value[i] ?? '');

  const write = (index: number, digit: string): void => {
    const next = digits.slice();
    next[index] = digit;
    onChange(next.join('').slice(0, SIZE));
    if (digit !== '' && index < SIZE - 1) boxes.current[index + 1]?.focus();
  };

  return (
    <div className={invalid ? 'code-row bad' : 'code-row'}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            boxes.current[index] = element;
          }}
          inputMode="numeric"
          maxLength={1}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`第 ${index + 1} 位`}
          value={digit}
          onChange={(event) => write(index, event.target.value.replace(/\D/g, '').slice(0, 1))}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' && digit === '' && index > 0) {
              event.preventDefault();
              write(index - 1, '');
              boxes.current[index - 1]?.focus();
            }
            if (event.key === 'ArrowLeft' && index > 0) {
              event.preventDefault();
              boxes.current[index - 1]?.focus();
            }
            if (event.key === 'ArrowRight' && index < SIZE - 1) {
              event.preventDefault();
              boxes.current[index + 1]?.focus();
            }
          }}
          onPaste={(event) => {
            event.preventDefault();
            const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, SIZE);
            if (pasted === '') return;
            onChange(pasted);
            boxes.current[Math.min(pasted.length, SIZE - 1)]?.focus();
          }}
        />
      ))}
    </div>
  );
}

/**
 * 「重新发送」的 60 秒冷却。
 *
 * 不只是防手抖：每次重发都会作废上一个码，狂点等于把自己刚收到的验证码作废掉。
 */
export function useResendCountdown(): { seconds: number; start: () => void } {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (seconds <= 0) return;
    const timer = setTimeout(() => setSeconds(seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);

  return { seconds, start: () => setSeconds(60) };
}
